import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Upload,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import OfficerService from "../../../services/OfficerService";

const { Option } = Select;

// Category options with bilingual labels
const CATEGORY_OPTIONS = [
  { value: "officebearer", label: "Office Bearer - पदाधिकारी" },
  { value: "gpmember", label: "GP Member - सदस्य" },
  { value: "gpofficer", label: "GP Officer - अधिकारी" },
  { value: "gpemployee", label: "GP Employee - इतर कर्मचारी" },
];

// Group labels with bilingual names
const GROUP_LABELS = {
  officebearer: "Office Bearer - पदाधिकारी",
  gpmember: "GP Member - सदस्य",
  gpofficer: "GP Officer - अधिकारी",
  gpemployee: "GP Employee - इतर कर्मचारी",
  other: "Other - इतर",
};

// Helper to get category label
const getCategoryLabel = (categoryValue) => {
  const category = CATEGORY_OPTIONS.find((c) => c.value === categoryValue);
  return category ? category.label : categoryValue || "-";
};

const Officers = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await OfficerService.getOfficers();
      setList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load officers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  // Group data by category and sort by order
  const groupedData = useMemo(() => {
    const groups = {
      officebearer: [],
      gpmember: [],
      gpofficer: [],
      gpemployee: [],
      other: [], // For any unrecognized categories
    };

    list.forEach((item) => {
      const category = item.tags?.toLowerCase();
      if (groups[category]) {
        groups[category].push(item);
      } else {
        groups.other.push(item);
      }
    });

    // Sort each group by order (ascending)
    const sortByOrder = (a, b) => (a.order || 0) - (b.order || 0);
    Object.keys(groups).forEach((key) => {
      groups[key].sort(sortByOrder);
    });

    // Return groups in specific order
    return [
      {
        key: "officebearer",
        groupName: GROUP_LABELS.officebearer,
        items: groups.officebearer,
        count: groups.officebearer.length,
      },
      {
        key: "gpmember",
        groupName: GROUP_LABELS.gpmember,
        items: groups.gpmember,
        count: groups.gpmember.length,
      },
      {
        key: "gpofficer",
        groupName: GROUP_LABELS.gpofficer,
        items: groups.gpofficer,
        count: groups.gpofficer.length,
      },
      {
        key: "gpemployee",
        groupName: GROUP_LABELS.gpemployee,
        items: groups.gpemployee,
        count: groups.gpemployee.length,
      },
      {
        key: "other",
        groupName: GROUP_LABELS.other,
        items: groups.other,
        count: groups.other.length,
      },
    ].filter((group) => group.items.length > 0);
  }, [list]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "Marathi" });
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      designation: record.designation,
      contact: record.contact,
      tags: record.tags,
      language: record.language || "Marathi",
      whatsapp: record.whatsapp,
      order: record.order,
    });
    setSelectedFile(null);
    setPreviewUrl(record.image || null);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete officer",
      content: "Are you sure?",
      async onOk() {
        try {
          await OfficerService.deleteOfficer(id);
          message.success("Deleted");
          fetch();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const beforeUpload = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      message.error("Invalid image type. Allowed: jpg, png, webp");
      return false;
    }
    if (file.size > maxSize) {
      message.error("Image must be smaller than 5MB");
      return false;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return false;
  };

  const onFinish = async (values) => {
    const payload = {
      name: values.name,
      designation: values.designation || "",
      contact: values.contact || "",
      tags: values.tags || "",
      language: values.language || "Marathi",
      whatsapp: values.whatsapp || "",
      order: values.order != null ? values.order : 0,
    };

    setLoading(true);
    try {
      if (editing) {
        await OfficerService.updateOfficer(editing.id, payload, selectedFile);
        message.success("Updated");
      } else {
        await OfficerService.createOfficer(payload, selectedFile);
        message.success("Created");
      }

      setIsModalOpen(false);
      form.resetFields();
      setSelectedFile(null);
      if (
        previewUrl &&
        previewUrl.startsWith &&
        previewUrl.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (e) {}
      }
      setPreviewUrl(null);
      fetch();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setSelectedFile(null);
    if (previewUrl && previewUrl.startsWith && previewUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (e) {}
    }
    setPreviewUrl(null);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (t) => (
        <div style={{ maxWidth: 200, whiteSpace: "normal" }}>{t}</div>
      ),
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      render: (t) => (
        <div style={{ maxWidth: 250, whiteSpace: "normal" }}>{t}</div>
      ),
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (v) => (v ? <Image src={v} width={60} /> : null),
    },
    { title: "Contact", dataIndex: "contact", key: "contact" },
    { title: "Whatsapp", dataIndex: "whatsapp", key: "whatsapp" },
    {
      title: "Category",
      dataIndex: "tags",
      key: "tags",
      render: (t) => getCategoryLabel(t),
    },
    { title: "Order", dataIndex: "order", key: "order" },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>
          Officers / Office Bearers (अधिकारी / पदाधिकारी)
        </h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
        </div>
      </div>

      {groupedData.length === 0 ? (
        <Table columns={columns} dataSource={[]} loading={loading} />
      ) : (
        groupedData.map((group) => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div
              style={{
                background: "linear-gradient(90deg, #001529 0%, #042035 100%)",
                padding: "12px 16px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "white", fontWeight: 600, fontSize: 16 }}>
                {group.groupName}
              </span>
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Total: {group.count}
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={group.items}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              style={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}
            />
          </div>
        ))
      )}

      <Modal
        title={editing ? "Edit Officer" : "Add Officer"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="name"
            label="Name (नाव)"
            rules={[{ required: true, message: "Enter name" }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item name="designation" label="Designation (पद)">
            <Input placeholder="Enter designation" />
          </Form.Item>

          <Form.Item label="Image (फोटो)">
            <Upload
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
            {previewUrl && (
              <div style={{ marginTop: 8 }}>
                <img src={previewUrl} alt="preview" style={{ maxWidth: 200 }} />
              </div>
            )}
          </Form.Item>

          <Form.Item name="tags" label="Category (श्रेणी)">
            <Select allowClear placeholder="Select a Category">
              {CATEGORY_OPTIONS.map((c) => (
                <Option key={c.value} value={c.value}>
                  {c.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="order" label="Order (क्रम)">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="Display order"
            />
          </Form.Item>

          <Form.Item name="contact" label="Contact (संपर्क)">
            <Input placeholder="Enter contact number" />
          </Form.Item>

          <Form.Item name="whatsapp" label="Whatsapp">
            <Input placeholder="Enter WhatsApp number" />
          </Form.Item>

          <Form.Item
            name="language"
            label="Language (भाषा)"
            initialValue="Marathi"
          >
            <Select>
              <Option value="English">English</Option>
              <Option value="Marathi">Marathi</Option>
              <Option value="Hindi">Hindi</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Officer Details"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {viewing && (
          <div>
            <p>
              <strong>Name:</strong> {viewing.name}
            </p>
            <p>
              <strong>Designation:</strong> {viewing.designation}
            </p>
            {viewing.image && (
              <p>
                <strong>Image:</strong>
                <br />
                <img
                  src={viewing.image}
                  alt="officer"
                  style={{ maxWidth: 200 }}
                />
              </p>
            )}
            <p>
              <strong>Contact:</strong> {viewing.contact}
            </p>
            <p>
              <strong>Whatsapp:</strong> {viewing.whatsapp}
            </p>
            <p>
              <strong>Category:</strong> {getCategoryLabel(viewing.tags)}
            </p>
            <p>
              <strong>Language:</strong> {viewing.language}
            </p>
            <p>
              <strong>Order:</strong> {viewing.order}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Officers;
